import { ChevronRightIcon } from '@heroicons/react/24/outline'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { FC, KeyboardEvent } from 'react'

import {
  RequestType,
  RequestTypeAlias
} from '@/api/models/requests/lib/requests.types'
import { ScreenSize } from '@/app/constants'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import { PageType } from '@/components/entities/requests/model/types'
import {
  formatRequestDate,
  getDisplayRequestDateEnd
} from '@/components/entities/requests/ui/RequestDetails/Content/lib/utils'
import { PassAuto } from '@/components/entities/requests/ui/RequestsList/PassAuto'
import { PassPersons } from '@/components/entities/requests/ui/RequestsList/PassPersons'
import { RequestDescription } from '@/components/entities/requests/ui/RequestsList/RequestDescription'
import { AppCopyToClipboard } from '@/components/shared/copy-to-clipboard'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { useRequestNavigation } from '@/components/widgets/request/hooks/useRequestNavigation'

import BrokenImageIcon from '../../assets/BrokenImageIcon.svg'
import { CommentsCounter } from '../RequestDetails/Content/CommentsCounter'
import RequestStatusBadge from '../RequestSummary/RequestStatusBadge'
import { PassDateRange } from './PassDateRange'

type Props = {
  request: RequestType
  pageType: PageType
}

const aliasesWithDescription = new Set([
  RequestTypeAlias.PermanentPassAuto,
  RequestTypeAlias.PermanentPassFamily,
  RequestTypeAlias.PermanentPassStaff,
  RequestTypeAlias.PermanentPassRenter,
  RequestTypeAlias.GuestPass,
  RequestTypeAlias.GuestPassAuto,
  RequestTypeAlias.PropertiesPass,
  RequestTypeAlias.CommonRequest,
  RequestTypeAlias.RentPassAuto
])

const aliasesWithCommentsCounter = new Set([
  RequestTypeAlias.PermanentPassAuto,
  RequestTypeAlias.PermanentPassFamily,
  RequestTypeAlias.PermanentPassStaff,
  RequestTypeAlias.PermanentPassRenter,
  RequestTypeAlias.CommonRequest
])

const RequestListItem: FC<Props> = ({ request, pageType }: Props) => {
  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [
    ScreenSize.Xxs,
    ScreenSize.Xs,
    ScreenSize.Sm
  ].includes(breakpoint)
  const pathname = usePathname()
  const { reachGoal } = useYandexMetrika()

  const { navigateToContentsFromDetails } = useRequestNavigation()

  const handleClickMoreInformation = () => {
    if (pathname === '/') {
      reachGoal(
        request.typeAlias === RequestTypeAlias.CommonRequest
          ? YandexMetrikaEvent.MAIN_NEW_REQUESTS_OPEN
          : YandexMetrikaEvent.MAIN_PASSES_OPEN
      )
    }
    navigateToContentsFromDetails(pageType, request.guid)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    handleClickMoreInformation()
  }

  const hasDescription =
    !!(request.autos || request.persons || request.description) &&
    aliasesWithDescription.has(request.typeAlias)

  const hasCommentsCounter =
    aliasesWithCommentsCounter.has(request.typeAlias) &&
    request.comments_count > 0

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={
        pageType === PageType.Passes
          ? 'pass-list-item-id'
          : 'request-list-item-id'
      }
      className="group mb-12 w-full rounded-16 bg-white p-16 text-left last:mb-0 sm:p-12"
      onClick={handleClickMoreInformation}
      onKeyDown={handleKeyDown}
    >
      <div className="flex flex-col gap-12 border-b-[1px] border-grey-20 pb-12 sm:pb-8">
        <div className="flex justify-between">
          <div
            className={cn(
              'flex items-center gap-12',
              !hasDescription && 'items-center'
            )}
          >
            <div className="relative flex shrink-0 cursor-pointer items-center justify-center rounded-12 bg-grey-10 p-10">
              {request.icon_link && (
                <Image
                  src={request.icon_link}
                  width={isMobileScreen ? 20 : 32}
                  height={20}
                  alt={request.title}
                />
              )}
              {request.isPayable && (
                <div className="absolute bottom-0 right-0 rounded-50 bg-white p-2">
                  <Image
                    src="/images/requests/subtract.svg"
                    alt="image"
                    width={16}
                    height={16}
                  />
                </div>
              )}
              {!request.icon_link && <BrokenImageIcon />}
            </div>

            <p className="font-bold text-20 sm:text-16">{request.title}</p>
          </div>

          <div className="flex items-center gap-30 md:gap-16">
            {hasCommentsCounter && (
              <CommentsCounter
                pageType={pageType}
                unreadCount={request.comments_count}
              />
            )}

            <ChevronRightIcon
              onClick={handleClickMoreInformation}
              className="h-24 w-24 text-deepBlack transition-colors hover:text-bronze-40 group-hover:text-bronze-40"
            />
          </div>
        </div>
        <div className="flex flex-col gap-4 break-words">
          {hasDescription && (
            <>
              {pageType === PageType.Passes && (
                <>
                  <PassAuto autos={request.autos} />
                  <PassPersons persons={request.persons} />
                  {request.desiredDateStart && request.desiredDateEnd && (
                    <PassDateRange
                      dateStart={request.desiredDateStart}
                      dateEnd={getDisplayRequestDateEnd(
                        request.typeAlias,
                        request.desiredDateEnd
                      )}
                      hideTodayLabel={
                        request.typeAlias === RequestTypeAlias.RentPassAuto
                      }
                    />
                  )}
                </>
              )}
              {pageType === PageType.Requests && (
                <RequestDescription description={request.description} />
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-12 sm:items-end sm:pt-8">
        <div className="flex items-center gap-20 sm:flex-col sm:items-start sm:gap-4">
          <div className="flex items-center">
            <p className="mr-4 text-16 sm:text-14">№ {request.id}</p>
            <AppCopyToClipboard
              text={String(request.id)}
              message={`Номер ${
                pageType === PageType.Passes ? 'пропуска' : 'обращения'
              } скопирован`}
            />
          </div>
          <p className="truncate text-16 sm:text-14">
            {formatRequestDate(request.date)}
          </p>
        </div>

        <RequestStatusBadge status={request.status} className="h-24" />
      </div>
    </div>
  )
}

export { RequestListItem }
